import { useEffect, useState } from "react";
import { formatMoney } from "./money";
import { failureMessage, MILESTONES } from "./orderCopy";
import { fetchOrderStatus, ORDER_STAGES, type OrderStageName, type OrderStatus } from "./orderStatus";

const POLL_MS = 2500;
const MAX_POLLS = 48; // ~2 minutes, then stop polling

// Payment is the only real prerequisite — everything else only starts once
// it clears. inventory/shipping/fraud are three independent EventBridge
// consumers of the same PaymentProcessed event with no ordering between
// them (docs/software-practices.md's saga section: "nothing sequences
// inventory before shipping") — shown as a group below, not a numbered
// sequence, so this view doesn't imply a promise the backend doesn't keep.
const PARALLEL_STAGES = ORDER_STAGES.filter((stage) => stage !== "payment");

export interface OrderSummary {
  orderId: string;
  itemCount: number;
  total: number;
  customerName?: string;
}

type Row =
  | { kind: "pending" } // not seen by the projection yet
  | { kind: "status"; status: OrderStatus }
  | { kind: "unavailable"; error: string };

type Phase = "polling" | "settled" | "timeout";

export function OrderConfirmation({
  summary,
  onDone,
}: {
  summary: OrderSummary;
  onDone: () => void;
}) {
  const [row, setRow] = useState<Row>({ kind: "pending" });
  const [phase, setPhase] = useState<Phase>("polling");

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    async function loop(n: number) {
      if (cancelled) return;
      const res = await fetchOrderStatus(summary.orderId);
      if (cancelled) return;

      if (res.kind === "unavailable") {
        setRow({ kind: "unavailable", error: res.error });
        setPhase("settled");
        return;
      }
      if (res.kind === "status") {
        setRow({ kind: "status", status: res.body });
        if (res.body.status !== "in_progress") {
          setPhase("settled");
          return;
        }
      }
      // not_found or still in_progress — keep polling
      if (n + 1 >= MAX_POLLS) {
        setPhase("timeout");
        return;
      }
      timer = window.setTimeout(() => void loop(n + 1), POLL_MS);
    }

    void loop(0);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [summary.orderId]);

  const status = row.kind === "status" ? row.status : undefined;

  return (
    <section className="confirmation">
      <p className="confirmation-eyebrow">Order placed</p>
      <h2 className="confirmation-headline">{headline(phase, row)}</h2>

      <p className="confirmation-summary">
        Order <code>{summary.orderId}</code> · {summary.itemCount}{" "}
        {summary.itemCount === 1 ? "item" : "items"} · {formatMoney(summary.total)}
        {summary.customerName ? ` · for ${summary.customerName}` : ""}
      </p>

      {row.kind === "unavailable" ? (
        <p className="confirmation-note">
          Your order went through — this environment just doesn't have live tracking
          switched on (<code>var.enable_order_status_read_model</code>).
        </p>
      ) : (
        <>
          <PaymentMilestone status={status} />

          <p className="milestones-parallel-label">Then, checked at the same time:</p>

          <ul className="milestones milestones-parallel">
            {PARALLEL_STAGES.map((stage) => {
              const state = milestoneState(status, stage);
              return (
                <li key={stage} className={`milestone milestone-${state}`}>
                  <MilestoneMark state={state} />
                  <MilestoneLabel stage={stage} state={state} />
                </li>
              );
            })}
          </ul>

          {status?.status === "failed" && (
            <div className="confirmation-problem">
              <strong>{status.failedStage ? MILESTONES[status.failedStage].label : "There was a problem"}</strong>
              <p>
                {failureMessage(
                  status.failedStage,
                  status.failedStage ? status.stages[status.failedStage]?.reason : undefined,
                )}
              </p>
            </div>
          )}

          {phase === "timeout" && status?.status === "in_progress" && (
            <p className="confirmation-note">
              Still working on it — this is taking longer than usual. We'll email you when
              it's done.
            </p>
          )}
        </>
      )}

      <button type="button" className="confirmation-continue" onClick={onDone}>
        Continue shopping
      </button>

      <p className="confirmation-footnote">
        Behind the scenes, this order ran as one distributed trace across five services in
        five languages.
      </p>
    </section>
  );
}

type MilestoneState = "done" | "failed" | "waiting" | "skipped";

function milestoneState(status: OrderStatus | undefined, stage: OrderStageName): MilestoneState {
  const s = status?.stages[stage];
  const failedHere = status?.status === "failed" && status.failedStage === stage;
  if (failedHere) return "failed";
  if (s?.result === "processed") return "done";
  if (s?.result === "failed") return "failed";
  if (status && status.status !== "in_progress") return "skipped";
  return "waiting";
}

function PaymentMilestone({ status }: { status: OrderStatus | undefined }) {
  const state = milestoneState(status, "payment");
  return (
    <div className={`milestone milestone-lead milestone-${state}`}>
      <MilestoneMark state={state} />
      <MilestoneLabel stage="payment" state={state} />
    </div>
  );
}

function MilestoneMark({ state }: { state: MilestoneState }) {
  return (
    <span className="milestone-mark" aria-hidden="true">
      {state === "done" ? "✓" : state === "failed" ? "✕" : state === "skipped" ? "–" : "…"}
    </span>
  );
}

function MilestoneLabel({ stage, state }: { stage: OrderStageName; state: MilestoneState }) {
  return (
    <span className="milestone-label">
      {state === "done" ? MILESTONES[stage].doneLabel : MILESTONES[stage].label}
      {state === "waiting" && " — in progress"}
      {state === "skipped" && " — not completed"}
    </span>
  );
}

function headline(phase: Phase, row: Row): string {
  if (row.kind === "unavailable") return "Thanks — your order is in";
  if (row.kind === "pending") return "We're getting your order ready…";
  const s = row.status;
  if (s.status === "fulfilled") return "Your order is confirmed 🎉";
  if (s.status === "failed") return "There's a problem with your order";
  return phase === "timeout" ? "Still processing your order" : "We're processing your order…";
}
